// Helpers for "fraction entry" answers (see FractionAnswer.tsx).

import {
  canonicalFraction,
  fractionEntry,
  type FractionAccept,
} from '../../platform/content/fractionAnswer';

// The three boxes: the whole number, the top number, the bottom number.
export type FractionPart = 'w' | 'n' | 'd';
export const FRACTION_PARTS: readonly FractionPart[] = ['w', 'n', 'd'];

// Each box takes a whole number up to 999.
export const FRACTION_MAX_LENGTH = 3;

// What a screen reader calls each box.
export const FRACTION_PART_NAMES: Record<FractionPart, string> = {
  w: 'Whole number, if there is one',
  n: 'Numerator',
  d: 'Denominator',
};

export function isFractionPart(id: unknown): id is FractionPart {
  return id === 'w' || id === 'n' || id === 'd';
}

// The practice screen keeps every answer as one piece of text. For a
// fraction that is three lines, "w=", "n=" and "d=", each with what was
// typed in that box -- exactly as typed; it is only tidied into the agreed
// fraction form when it is sent. When the minus-sign button is on (questions
// with negatives, 2 Nov 2026), a fourth line "s=-" follows.
export type FractionParts = Record<FractionPart, string> & {
  negative?: boolean;
};

const NEGATIVE_LINE = 's=-';

export function splitFractionParts(value: string): FractionParts {
  const parts: FractionParts = { w: '', n: '', d: '' };
  for (const line of value.split('\n')) {
    const id = line.slice(0, 1);
    if (isFractionPart(id) && line.charAt(1) === '=') parts[id] = line.slice(2);
    else if (line === NEGATIVE_LINE) parts.negative = true;
  }
  return parts;
}

// Empty text while every box is empty and the minus sign is off, so "nothing
// answered yet" reads the same as it does for every other kind of question.
export function joinFractionParts(parts: FractionParts): string {
  const negative = parts.negative === true;
  if (!negative && FRACTION_PARTS.every((id) => parts[id].length === 0))
    return '';
  const lines = FRACTION_PARTS.map((id) => `${id}=${parts[id]}`);
  if (negative) lines.push(NEGATIVE_LINE);
  return lines.join('\n');
}

// The minus-sign button: the same answer text with the sign turned on or off.
export function setFractionNegative(value: string, negative: boolean): string {
  return joinFractionParts({ ...splitFractionParts(value), negative });
}

// What a box keeps of what was typed or pasted into it: digits only, three
// at most.
export function fractionDigits(text: string): string {
  return text.replace(/[^0-9]/g, '').slice(0, FRACTION_MAX_LENGTH);
}

// One press on the number pad, for the box it types into.
export function pressFractionKey(text: string, key: string): string {
  return fractionDigits(text + key);
}

// What is sent: the one entry, "f=<fraction>". Null until the top and the
// bottom numbers make a fraction. "f=-3/4" with the minus sign on.
export function fractionOrder(
  value: string,
  accept: FractionAccept,
): string[] | null {
  const parts = splitFractionParts(value);
  const canonical = canonicalFraction(
    {
      whole: parts.w,
      numerator: parts.n,
      denominator: parts.d,
      negative: parts.negative === true,
    },
    accept,
  );
  return canonical === null ? null : [fractionEntry(canonical)];
}

// A bottom number of 0 ("0", "00") can never be a fraction. An empty box is
// simply not finished.
export function fractionBottomIsZero(value: string): boolean {
  return /^0+$/.test(splitFractionParts(value).d);
}
